/**
 * Gates shared by the backend publish endpoint and the admin preflight for
 * the Active Investor Index feed (schema seedforge.w6-feed.v1). The feed is
 * produced by the weekly investor-extraction routine outside the platform.
 */
export const INVESTOR_INDEX_FEED_SCHEMA = 'seedforge.w6-feed.v1';
/** Certification statuses that may reach production. */
export const PUBLISHABLE_CERTIFICATION_STATUSES = ['CERTIFIED', 'CERTIFIED_WITH_QUARANTINE'];
/** A certification older than this must be re-run before its feed publishes. */
export const MAX_CERTIFICATION_AGE_DAYS = 21;
/**
 * Share of currently published pages a single feed may de-publish without an
 * explicit confirmation. A feed that drops more than this is far more likely
 * to be a broken export than a real loss of density.
 */
export const LARGE_DEPUBLISH_SHARE = 0.1;
/** Error code the publish endpoint returns when that confirmation is missing. */
export const LARGE_DEPUBLISH_ERROR_CODE = 'LARGE_DEPUBLISH';
export function isPublishableCertification(status) {
    return PUBLISHABLE_CERTIFICATION_STATUSES.includes(status);
}
/**
 * Whole days between a `YYYY-MM-DD` certification date (UTC) and `now`.
 * Negative when the date is in the future; NaN when it does not parse.
 */
export function certificationAgeDays(certificationDate, now = new Date()) {
    const certified = new Date(`${certificationDate}T00:00:00Z`);
    // `Date` silently rolls impossible dates forward (2026-02-31 becomes March 3);
    // a date that does not round-trip is not a real calendar date.
    if (Number.isNaN(certified.getTime()) ||
        certified.toISOString().slice(0, 10) !== certificationDate) {
        return Number.NaN;
    }
    return Math.floor((now.getTime() - certified.getTime()) / 86_400_000);
}
/**
 * The date a feed row's claim is about: the researched deal date (`dated_on`),
 * falling back to the evidence post's date for rows or feeds without one. The
 * site displays, sorts and ages rows by this; `announced_on` alone only says
 * when the evidence was posted.
 */
export function investorIndexDealDate(row) {
    return row.dated_on ?? row.announced_on;
}
// ─── Public URLs ──────────────────────────────────────────────────────
// One definition of every Active Investor Index URL, used by the backend when
// it rewrites a feed's links at import and by the site's routes and hrefs.
/** The hub; list pages live beneath it. */
export const INVESTOR_INDEX_HUB_PATH = '/investors/active';
/**
 * A platform-minted investor id: 6 characters of lowercase Crockford base32,
 * which leaves out i, l, o and u so an id cannot be misread.
 */
export const INVESTOR_INDEX_PUBLIC_ID_PATTERN = /^[0-9a-hjkmnp-tv-z]{6}$/;
/** Separates the cosmetic slug from the id; slugs never contain it. */
const ID_SEPARATOR = '--';
export function investorIndexListPath(sector, stage) {
    return `${INVESTOR_INDEX_HUB_PATH}/${sector}/${stage}`;
}
export function investorIndexFundPath(slug, publicId) {
    return `/investors/funds/${slug}${ID_SEPARATOR}${publicId}`;
}
/**
 * Evidence for one deal (`dealKey` given) or, without it, the investor's latest
 * evidence. Only the deal form names a fixed fact; the investor form moves when
 * the investor gets a newer deal.
 */
export function investorIndexEvidencePath(slug, publicId, dealKey) {
    const base = `${investorIndexFundPath(slug, publicId)}/evidence`;
    return dealKey ? `${base}/${dealKey}` : base;
}
/**
 * Splits a `<slug>--<id>` route segment. The id is what identifies the
 * investor; the slug is only for people and is corrected by redirect.
 */
export function parseInvestorIndexFundSegment(segment) {
    const at = segment.lastIndexOf(ID_SEPARATOR);
    if (at <= 0)
        return null;
    const publicId = segment.slice(at + ID_SEPARATOR.length);
    if (!INVESTOR_INDEX_PUBLIC_ID_PATTERN.test(publicId))
        return null;
    return { slug: segment.slice(0, at), publicId };
}
//# sourceMappingURL=investor-index-feed.js.map